import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  TableCell,
  TableRow,
  Grid,
  Paper,
  Autocomplete,
} from '@mui/material';
import SettingsIcon from '@mui/icons-material/Settings';
import ClearIcon from '@mui/icons-material/Clear';
import api from '../config/api';
import DataTable from '../components/DataTable';

function TrainerInteractions() {
  const [interactions, setInteractions] = useState([]);
  const [trainers, setTrainers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState({ total_spend: 0 });
  const [rules, setRules] = useState({
    contact_view_fee: '',
    max_trainers_per_user: '',
    max_trainers_view_user: '',
    initial_trainer_coins: '',
  });
  const [open, setOpen] = useState(false);
  const [error, setError] = useState('');
  
  // Filter states
  const [filterTrainer, setFilterTrainer] = useState('');
  const [filterMobile, setFilterMobile] = useState('');
  const [filterStartDate, setFilterStartDate] = useState('');
  const [filterEndDate, setFilterEndDate] = useState('');

  useEffect(() => {
    fetchInteractions();
    fetchTrainers();
    fetchRules();
  }, []);

  useEffect(() => {
    fetchInteractions();
  }, [filterTrainer, filterMobile, filterStartDate, filterEndDate]);

  const fetchInteractions = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (filterTrainer) params.append('trainer_id', filterTrainer);
      if (filterMobile) params.append('mobile', filterMobile);
      if (filterStartDate) params.append('start_date', filterStartDate);
      if (filterEndDate) params.append('end_date', filterEndDate);
      
      const response = await api.get(`/trainer-interactions?${params.toString()}`);
      setInteractions(response.data.data || []);
      setSummary(response.data.summary || { total_spend: 0 });
    } catch (error) {
      console.error('Error fetching interactions:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchTrainers = async () => {
    try {
      const response = await api.get('/trainers');
      setTrainers(response.data.data || []);
    } catch (error) {
      console.error('Error fetching trainers:', error);
    }
  };

  const fetchRules = async () => {
    try {
      const response = await api.get('/trainer-interactions/rules');
      setRules(response.data.data || {});
    } catch (error) {
      console.error('Error fetching rules:', error);
    }
  };

  const handleOpen = () => {
    setOpen(true);
    setError('');
  };

  const handleClose = () => {
    setOpen(false);
    setError('');
  };

  const handleSubmit = async () => {
    try {
      setError('');
      await api.put('/trainer-interactions/rules', rules);
      handleClose();
      fetchRules();
    } catch (error) {
      setError(error.response?.data?.error || 'Error updating rules');
    }
  };

  const handleClearFilters = () => {
    setFilterTrainer('');
    setFilterMobile('');
    setFilterStartDate('');
    setFilterEndDate('');
  };

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">Trainer Interactions</Typography>
        {/* <Button
          variant="contained"
          startIcon={<SettingsIcon />}
          onClick={handleOpen}
        >
          Manage Rules
        </Button> */}
      </Box>

      {/* Total Spend Summary */}
      <Paper sx={{ p: 2, mb: 3, bgcolor: 'white', color: '#000' }}>
        <Typography variant="h6">Total Spend Amount</Typography>
        <Typography variant="h4">{summary.total_spend.toFixed(2)} Coins</Typography>
        <Typography variant="caption">
          {filterTrainer || filterMobile || filterStartDate || filterEndDate 
            ? 'Based on applied filters' 
            : 'All interactions'}
        </Typography>
      </Paper>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="h6" gutterBottom>Filters</Typography>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={3}>
            <Autocomplete
              options={trainers}
              getOptionLabel={(trainer) => `${trainer.name || ''}${trainer.email ? ` (${trainer.email})` : ''}`}
              value={trainers.find((t) => String(t.id) === String(filterTrainer)) || null}
              onChange={(_, newValue) => setFilterTrainer(newValue ? String(newValue.id) : '')}
              isOptionEqualToValue={(option, value) => option?.id === value?.id}
              renderInput={(params) => (
                <TextField {...params} label="Trainer" placeholder="Search trainer..." />
              )}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              label="Mobile Number"
              value={filterMobile}
              onChange={(e) => setFilterMobile(e.target.value)}
              placeholder="Search by mobile"
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              label="Start Date"
              type="date"
              value={filterStartDate}
              onChange={(e) => setFilterStartDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              label="End Date"
              type="date"
              value={filterEndDate}
              onChange={(e) => setFilterEndDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12}>
            <Button
              variant="outlined"
              startIcon={<ClearIcon />}
              onClick={handleClearFilters}
            >
              Clear Filters
            </Button>
          </Grid>
        </Grid>
      </Paper>

      <DataTable
        data={interactions}
        loading={loading}
        searchFields={['trainer_name', 'user_name', 'mobile']}
        emptyMessage="No interactions found"
        columns={[
          { field: 'id', label: 'ID' },
          { field: 'trainer_name', label: 'Trainer' },
          { field: 'user_name', label: 'User' },
          { field: 'mobile', label: 'Mobile Number' },
          { field: 'interaction_type', label: 'Interaction Type' },
          { field: 'coins_deducted', label: 'Coins Deducted' },
          { field: 'created_at', label: 'Date' },
        ]}
        renderRow={(interaction) => (
          <TableRow key={interaction.id}>
            <TableCell>{interaction.id}</TableCell>
            <TableCell>{interaction.trainer_name}</TableCell>
            <TableCell>{interaction.user_name}</TableCell>
            <TableCell>{interaction.mobile || '-'}</TableCell>
            <TableCell>
              <Chip
                label={interaction.interaction_type}
                size="small"
                color={
                  interaction.interaction_type === 'respond'
                    ? 'success'
                    : 'default'
                }
              />
            </TableCell>
            <TableCell>{interaction.coins_deducted || 0}</TableCell>
            <TableCell>
              {new Date(interaction.created_at).toLocaleString()}
            </TableCell>
          </TableRow>
        )}
      />

      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>Manage Interaction Rules</DialogTitle>
        <DialogContent>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          <TextField
            fullWidth
            label="Contact View Fee (Coins)"
            type="number"
            value={rules.contact_view_fee}
            onChange={(e) =>
              setRules({ ...rules, contact_view_fee: e.target.value })
            }
            margin="normal"
            helperText="Coins deducted when trainer views user contact"
          />
          <TextField
            fullWidth
            label="Max Trainers Per User"
            type="number"
            value={rules.max_trainers_per_user}
            onChange={(e) =>
              setRules({ ...rules, max_trainers_per_user: e.target.value })
            }
            margin="normal"
            helperText="Maximum number of trainers who can respond to each user"
          />
          <TextField
            fullWidth
            label="Max Trainers View User"
            type="number"
            value={rules.max_trainers_view_user || ''}
            onChange={(e) =>
              setRules({ ...rules, max_trainers_view_user: e.target.value })
            }
            margin="normal"
            helperText="Maximum number of trainers who can view each user's contact (leave empty to disable limit)"
          />
          <TextField
            fullWidth
            label="Initial Trainer Coins"
            type="number"
            value={rules.initial_trainer_coins}
            onChange={(e) =>
              setRules({ ...rules, initial_trainer_coins: e.target.value })
            }
            margin="normal"
            helperText="Initial coins added to trainer account on registration"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained">
            Update Rules
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default TrainerInteractions;

